import { THREATS, type Threat } from '@cyberscore/contracts';
import { Check, Eye, FileText, ListChecks, Minus, Send, Umbrella, X } from 'lucide-react';
import { href } from '../../lib/router';
import { useSession } from '../../lib/session';
import { industryLabel } from '../assessments/AssessmentsPage';
import { bandLabel } from '../portfolio/format';
import { Ring } from './charts';
import { useBusiness, useBusinessScore, useOwnsBusiness } from './context';
import { CardHead } from './parts';
import { NoAssessment, ScoreProblem } from './states';

/**
 * Insurance readiness, as the prototype lays it out: the insurer's minimum requirements (the kill-switch
 * protections), what the insurer sees, and handing the assessment over.
 */
export function InsurancePage() {
  const { tenant } = useSession();
  // A business on its own has no insurer here yet: the snapshot is what one would see when it shares it.
  const own = useOwnsBusiness();
  const { loading } = useBusiness();
  const { current: a, view, derived } = useBusinessScore();
  if (loading) return <div className="empty">Loading…</div>;
  if (!a) return <NoAssessment />;
  if (view.isPending) return <div className="empty">Loading…</div>;
  if (view.isError || !derived) return <ScoreProblem a={a} error={view.error} />;

  const r = view.data.score;
  const reqs = derived.requirements;
  const met = reqs.filter((q) => q.state === 'met').length;
  const ready = reqs.length > 0 && met === reqs.length;
  const submitted = a.status !== 'in_progress';
  const snap: [string, string][] = [
    ['CyberScore', `${r.score} / 1000 · ${bandLabel(r.band)}`],
    ['Verified', `${Math.round(r.controls.evidence_confidence * 100)}%`],
    ['Minimum requirements', `${met} of ${reqs.length} met`],
    ['Underwriting exceptions', r.kill_switches.failed.length ? `${r.kill_switches.failed.length} open` : 'None'],
    ['Score cap', r.kill_switches.cap === null ? 'None' : String(r.kill_switches.cap)],
    ['Protections answered', `${r.controls.answered} of ${r.controls.applicable}`],
    ...(Object.keys(THREATS) as Threat[]).map((t): [string, string] => [`${THREATS[t].name} resilience`, String(Math.round(r.resilience[t]))]),
    ['Industry', industryLabel(a.context.industry)],
  ];

  return (
    <>
      <div className="bpage-head">
        <div>
          <h1>Insurance readiness</h1>
          <p>Insurers ask for a few minimum protections before anything else. You see simple ticks; they see verified detail.</p>
        </div>
      </div>
      <div className="bgrid g-side">
        <div className="stack">
          <section className={`bcard${ready ? ' okbg' : ''}`}>
            <div className="row wrap gap-l">
              <Ring value={reqs.length ? (100 * met) / reqs.length : 0} color={ready ? 'var(--ok-strong)' : 'var(--primary)'} size={108} stroke={11} />
              <div className="grow minw">
                <div className="eyebrow">{ready ? 'Insurance ready' : 'Almost there'}</div>
                <h2 className="mt-xs">{met} of {reqs.length} requirements met</h2>
                <p className="muted mt-s">
                  {ready
                    ? 'Your security may qualify you for better cyber insurance terms. Your insurer confirms the final terms.'
                    : 'Close the remaining gaps so your insurer can quote with fewer questions.'}
                </p>
              </div>
              {submitted ? (
                <span className="bbadge ok">{own ? 'Finished' : 'Submitted'}{a.submitted_at ? ` ${new Date(a.submitted_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}` : ''}</span>
              ) : (
                <a className={`bbtn ${ready ? 'pri' : 'dark'}`} href={href.assessment(a.id)}>
                  <Send size={15} /> {r.provisional ? 'Finish the Cyber Check' : own ? 'Review and finish' : 'Review and submit'}
                </a>
              )}
            </div>
          </section>
          <section className="bcard">
            <CardHead icon={<ListChecks size={18} />} title="Minimum requirements" sub="Protections that cap your score when missing, whatever else you have" />
            {reqs.map((q) => (
              <div key={q.control} className="req-row">
                <span className={`ci ${q.state === 'met' ? 'ok' : q.state === 'failed' ? 'bad' : 'warn'}`}>
                  {q.state === 'met' ? <Check size={14} /> : q.state === 'failed' ? <X size={14} /> : <Minus size={14} />}
                </span>
                <span className="grow">
                  {q.name}
                  {q.state === 'open' && <span className="muted small"> · not confirmed yet</span>}
                </span>
                {q.state !== 'met' && !submitted && q.question && (
                  <a className="bbtn xs pri" href={href.answer(a.id, q.question)}>
                    {q.state === 'failed' ? 'Fix' : 'Answer'}{q.points > 0 ? ` · +${q.points}` : ''}
                  </a>
                )}
              </div>
            ))}
            {!reqs.length && <p className="muted small">No minimum requirements apply to this assessment yet.</p>}
          </section>
        </div>
        <div className="stack">
          <section className="bcard">
            <CardHead tone="acc" icon={<Eye size={18} />} title={own ? 'What an insurer would see' : 'What your insurer sees'} sub="The underwriting snapshot of this assessment" />
            <dl className="kv">
              {snap.map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd className="num">{v}</dd>
                </div>
              ))}
            </dl>
            <hr className="divider" />
            <p className="muted small">{own ? 'Only your team sees your answers and documents. Share your score with an insurer or broker when you choose; prices and terms are set by them, not by CyberScore.' : 'Your documents are only seen by the people working on this assessment. Prices and terms are set by your insurer, not by CyberScore.'}</p>
          </section>
          {own ? (
            <a className="bcard soft block-link" href={href.report()}>
              <div className="row gap">
                <FileText size={22} />
                <div>
                  <b>Your CyberScore report</b>
                  <div className="muted small">The score, what holds it down and what to fix first, ready to print or share.</div>
                </div>
              </div>
            </a>
          ) : (
            <section className="bcard soft">
              <div className="row gap">
                <Umbrella size={22} />
                <div>
                  <b>Your insurer</b>
                  <div className="muted small">{tenant?.name}</div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
} // prettier-ignore
